<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import TagInput from 'dashboard/components-next/taginput/TagInput.vue';

const props = defineProps({
  pipeline: {
    type: Object,
    required: true,
  },
});

const store = useStore();

const selectedAgentIds = ref([]);
const selectedInboxIds = ref([]);
const isUpdatingAgents = ref(false);
const isUpdatingInboxes = ref(false);

const accountId = computed(() => store.getters.getCurrentAccountId);
const agentList = computed(() => store.getters['agents/getAgents']);
const inboxList = computed(() => store.getters['inboxes/getInboxes']);

const selectedAgentNames = computed(() =>
  selectedAgentIds.value.map(
    id => agentList.value.find(a => a.id === id)?.name ?? ''
  )
);

const selectedInboxNames = computed(() =>
  selectedInboxIds.value.map(
    id => inboxList.value.find(i => i.id === id)?.name ?? ''
  )
);

const agentMenuItems = computed(() =>
  agentList.value
    .filter(a => !selectedAgentIds.value.includes(a.id))
    .map(a => ({
      label: a.name,
      value: a.id,
      action: 'select',
      thumbnail: { name: a.name, src: a.thumbnail || a.avatar_url || '' },
    }))
);

const inboxMenuItems = computed(() =>
  inboxList.value
    .filter(i => !selectedInboxIds.value.includes(i.id))
    .map(i => ({
      label: i.name,
      value: i.id,
      action: 'select',
    }))
);

const handleAgentAdd = ({ value }) => {
  if (!selectedAgentIds.value.includes(value)) {
    selectedAgentIds.value.push(value);
  }
};

const handleAgentRemove = index => {
  selectedAgentIds.value.splice(index, 1);
};

const handleInboxAdd = ({ value }) => {
  if (!selectedInboxIds.value.includes(value)) {
    selectedInboxIds.value.push(value);
  }
};

const handleInboxRemove = index => {
  selectedInboxIds.value.splice(index, 1);
};

const fetchPipelineMembers = async () => {
  try {
    const response = await store.dispatch('pipelineMembers/get', {
      accountId: accountId.value,
      pipelineId: props.pipeline.id,
    });
    const members = response.data.payload || [];
    selectedAgentIds.value = members.map(m => m.id);
  } catch {
    // silently handle
  }
};

const fetchPipelineInboxes = async () => {
  try {
    const response = await store.dispatch('pipelineInboxes/get', {
      accountId: accountId.value,
      pipelineId: props.pipeline.id,
    });
    const inboxes = response.data.payload || [];
    selectedInboxIds.value = inboxes.map(i => i.id);
  } catch {
    // silently handle
  }
};

const updateAgents = async () => {
  isUpdatingAgents.value = true;
  try {
    await store.dispatch('pipelineMembers/update', {
      accountId: accountId.value,
      pipelineId: props.pipeline.id,
      userIds: selectedAgentIds.value,
    });
    useAlert('Agentes atualizados com sucesso!');
  } catch {
    useAlert('Erro ao atualizar agentes');
  } finally {
    isUpdatingAgents.value = false;
  }
};

const updateInboxes = async () => {
  isUpdatingInboxes.value = true;
  try {
    await store.dispatch('pipelineInboxes/update', {
      accountId: accountId.value,
      pipelineId: props.pipeline.id,
      inboxIds: selectedInboxIds.value,
    });
    useAlert('Caixas de entrada atualizadas com sucesso!');
  } catch {
    useAlert('Erro ao atualizar caixas de entrada');
  } finally {
    isUpdatingInboxes.value = false;
  }
};

onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('inboxes/get');
  fetchPipelineMembers();
  fetchPipelineInboxes();
});
</script>

<template>
  <div class="space-y-2">
    <!-- Agents Section -->
    <SettingsFieldSection
      label="Agentes com Acesso"
      help-text="Selecione quais agentes podem acessar este pipeline. Se nenhum for selecionado, todos terão acesso."
    >
      <div
        class="rounded-xl outline outline-1 -outline-offset-1 outline-n-weak hover:outline-n-strong px-2 py-2"
      >
        <TagInput
          :model-value="selectedAgentNames"
          placeholder="Selecione os agentes..."
          :menu-items="agentMenuItems"
          show-dropdown
          skip-label-dedup
          :auto-open-dropdown="false"
          @add="handleAgentAdd"
          @remove="handleAgentRemove"
        />
      </div>

      <template #extra>
        <div class="grid grid-cols-1 lg:grid-cols-8">
          <div class="col-span-1 lg:col-span-2" />
          <div class="col-span-1 lg:col-span-6 mt-4 justify-self-end">
            <Button
              :is-loading="isUpdatingAgents"
              :disabled="isUpdatingAgents"
              @click="updateAgents"
            >
              Atualizar Agentes
            </Button>
          </div>
        </div>
      </template>
    </SettingsFieldSection>

    <!-- Inboxes Section -->
    <div class="border-t border-n-weak pt-2">
      <SettingsFieldSection
        label="Caixas de Entrada"
        help-text="Selecione de quais caixas de entrada os deals podem ser criados. Se nenhuma for selecionada, todas serão permitidas."
      >
        <div
          class="rounded-xl outline outline-1 -outline-offset-1 outline-n-weak hover:outline-n-strong px-2 py-2"
        >
          <TagInput
            :model-value="selectedInboxNames"
            placeholder="Selecione as caixas de entrada..."
            :menu-items="inboxMenuItems"
            show-dropdown
            skip-label-dedup
            :auto-open-dropdown="false"
            @add="handleInboxAdd"
            @remove="handleInboxRemove"
          />
        </div>

        <template #extra>
          <div class="grid grid-cols-1 lg:grid-cols-8">
            <div class="col-span-1 lg:col-span-2" />
            <div class="col-span-1 lg:col-span-6 mt-4 justify-self-end">
              <Button
                :is-loading="isUpdatingInboxes"
                :disabled="isUpdatingInboxes"
                @click="updateInboxes"
              >
                Atualizar Caixas de Entrada
              </Button>
            </div>
          </div>
        </template>
      </SettingsFieldSection>
    </div>
  </div>
</template>
